import { ApiErrorSchema } from "@nexus/types/contracts"
import { CONVERSATION_CLOSED_CODE } from "@nexus/types/domain"

/**
 * A chat request the backend refused, carrying the two facts the widget decides on:
 * the HTTP status and the envelope's machine-readable `error.code`.
 *
 * The chat doors used to throw a bare `Error(message)`, which kept the prose and
 * dropped the code — so "this conversation is closed" and "the server is down"
 * reached the widget as two sentences and nothing it could switch on. Those call
 * for opposite responses: the first re-initialises into a fresh conversation, the
 * second keeps its existing handling. `message` is unchanged from what these doors
 * threw before, so every reader of the prose is unaffected.
 */
export class EmbedRequestError extends Error {
  readonly status: number
  /** `error.code` from the canonical envelope, or `null` when the body carried none. */
  readonly code: string | null

  constructor(message: string, status: number, code: string | null) {
    super(message)
    this.name = "EmbedRequestError"
    this.status = status
    this.code = code
  }
}

/**
 * The serializable form a thunk rejects with. An `Error` instance cannot ride a
 * Redux action, so the code travels as a plain field beside the message.
 */
export interface EmbedRequestFailure {
  readonly message: string
  readonly code: string | null
}

/** Where the thrown message comes from when the body names one. */
type MessageSource = "server" | "fallback"

/**
 * Read a failed response into an {@link EmbedRequestError}.
 *
 * `messageSource: "server"` prefers the message the server wrote (`error.message`,
 * then a top-level `message`, then a non-JSON body verbatim) and falls back to
 * `fallbackMessage`; `"fallback"` always throws `fallbackMessage`. The code is read
 * either way — it is the one thing a caller may branch on.
 */
export async function readEmbedRequestError(
  response: Response,
  fallbackMessage: string,
  messageSource: MessageSource = "server"
): Promise<EmbedRequestError> {
  const body = await response.text().catch(() => "")
  const parsed = parseJson(body)
  const message =
    messageSource === "server" ? serverMessage(parsed, body, fallbackMessage) : fallbackMessage

  return new EmbedRequestError(message, response.status, envelopeCode(parsed))
}

/**
 * True when `error` is the backend refusing a closed conversation.
 *
 * Keyed on the CODE alone, never the status or the message: a 403 is also what an
 * authentication refusal answers, and the message is prose that may be reworded.
 */
export function isConversationClosed(error: unknown): boolean {
  return error instanceof EmbedRequestError && error.code === CONVERSATION_CLOSED_CODE
}

/** Flatten a caught value into the payload a thunk rejects with. */
export function toEmbedRequestFailure(
  error: unknown,
  fallbackMessage: string
): EmbedRequestFailure {
  return {
    message: error instanceof Error ? error.message : fallbackMessage,
    code: error instanceof EmbedRequestError ? error.code : null
  }
}

/** `undefined` for a body that is not JSON — an error response is not certainly JSON. */
function parseJson(body: string): unknown {
  try {
    return JSON.parse(body) as unknown
  } catch {
    return undefined
  }
}

function envelopeCode(parsed: unknown): string | null {
  const envelope = ApiErrorSchema.safeParse(parsed)
  return envelope.success ? envelope.data.error.code : null
}

function serverMessage(parsed: unknown, body: string, fallbackMessage: string): string {
  if (parsed === undefined) return body || fallbackMessage
  if (typeof parsed !== "object" || parsed === null) return fallbackMessage

  const nested = "error" in parsed ? parsed.error : undefined
  const nestedMessage =
    typeof nested === "object" && nested !== null && "message" in nested
      ? nested.message
      : undefined
  if (typeof nestedMessage === "string" && nestedMessage) return nestedMessage

  const topMessage = "message" in parsed ? parsed.message : undefined
  return typeof topMessage === "string" && topMessage ? topMessage : fallbackMessage
}
